import { defineStore } from 'pinia';
import * as fennecService from '../services/fennec.service';
import { useFeatureFlagsStore } from '@/shared/store/featureFlagsStore';
import {
  FENNEC_STORAGE_KEY,
  FENNEC_SESSION_KEY,
  FENNEC_FEED_PAGE_SIZE,
  FENNEC_AUTH_ROUTES,
} from '../constants/fennec.constants';

function getSessionId() {
  let sid = sessionStorage.getItem(FENNEC_SESSION_KEY);
  if (!sid) {
    sid = `fennec_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(FENNEC_SESSION_KEY, sid);
  }
  return sid;
}

const FENNEC_FEED_ROUTES = new Set(['social.list']);

export const useFennecStore = defineStore('fennec', {
  state: () => ({
    featureEnabled: true,
    featureFlagLoaded: false,
    fennecEnabled: localStorage.getItem(FENNEC_STORAGE_KEY) === 'true',
    onFeedPage: false,
    onAuthPage: false,
    status: null,
    loading: false,
    toggling: false,
    feedLoading: false,
    feedLoadingMore: false,
    feed: [],
    cursor: null,
    hasMore: true,
    pinnedWidget: null,
    context: 'NO_MATCH',
    error: null,
    initialized: false,
    firstActivation: !localStorage.getItem(FENNEC_STORAGE_KEY),
    fennecStoryIds: { officialStoryIds: [], regularStoryIds: [], allFennecStoryIds: [] },
    onboardingShown: localStorage.getItem('fennec_onboarding_shown') === 'true',
  }),

  getters: {
    isActive: (state) => state.featureEnabled && state.fennecEnabled && state.onFeedPage,
    isFeatureAvailable: (state) => state.featureEnabled,
    isFennecOn: (state) => state.featureEnabled && state.fennecEnabled,
    /** Toggle on but excluded on auth pages (no green tokens / Fennec logos) */
    isFennecPlatformActive: (state) =>
      state.featureEnabled && state.fennecEnabled && !state.onAuthPage,
    currentContext: (state) => state.context,
    feedItems: (state) => state.feed,
    sessionId: () => getSessionId(),
  },

  actions: {
    async fetchFeatureFlag() {
      const flagsStore = useFeatureFlagsStore();
      if (!flagsStore.loaded) {
        await flagsStore.fetchFlags();
      }
      this.featureEnabled = flagsStore.fennecMode;
      this.featureFlagLoaded = true;

      if (!this.featureEnabled && this.fennecEnabled) {
        this.fennecEnabled = false;
        localStorage.removeItem(FENNEC_STORAGE_KEY);
        document.body.classList.remove('fennec-active');
      }
    },

    async fetchStatus() {
      const flagsStore = useFeatureFlagsStore();
      if (!flagsStore.loaded) {
        await flagsStore.fetchFlags();
      }
      if (!flagsStore.fennecMode) {
        this.featureEnabled = false;
        this.fennecEnabled = false;
        localStorage.removeItem(FENNEC_STORAGE_KEY);
        document.body.classList.remove('fennec-active');
        this.initialized = true;
        return;
      }

      const result = await fennecService.getStatus();
      if (result.success && result.data) {
        if (result.data.featureDisabled) {
          this.featureEnabled = false;
          this.fennecEnabled = false;
          localStorage.removeItem(FENNEC_STORAGE_KEY);
          document.body.classList.remove('fennec-active');
        } else {
          this.featureEnabled = true;
          this.status = result.data;
          this.fennecEnabled = result.data.fennecEnabled;
          localStorage.setItem(FENNEC_STORAGE_KEY, String(this.fennecEnabled));
          this.syncBodyClass();
        }
      }
      this.initialized = true;
    },

    async toggle() {
      this.toggling = true;
      const newState = !this.fennecEnabled;
      this.fennecEnabled = newState;
      localStorage.setItem(FENNEC_STORAGE_KEY, String(newState));

      try {
        const result = await fennecService.toggleFennec(newState);
        if (result.success && result.data) {
          this.status = result.data;
        }

        await fennecService.trackEvent(
          newState ? 'TOGGLE_ON' : 'TOGGLE_OFF',
          this.sessionId,
        );

        if (newState) {
          this.firstActivation = false;
          const pinnedResult = await fennecService.getPinnedWidget();
          if (pinnedResult.success) {
            this.pinnedWidget = pinnedResult.data;
          }
        } else {
          this.feed = [];
          this.cursor = null;
          this.hasMore = true;
          this.pinnedWidget = null;
        }
      } finally {
        this.toggling = false;
        this.syncBodyClass();
      }
    },

    async loadFeed() {
      if (this.feedLoading) return;
      this.feedLoading = true;
      this.error = null;

      try {
        const result = await fennecService.getFeed(null, FENNEC_FEED_PAGE_SIZE);
        if (result.success && result.data) {
          this.feed = result.data.items || [];
          this.cursor = result.data.nextCursor;
          this.hasMore = !!result.data.nextCursor;
          this.context = result.data.context || 'NO_MATCH';
        } else {
          this.error = result.message;
        }

        const pinnedResult = await fennecService.getPinnedWidget();
        if (pinnedResult.success) {
          this.pinnedWidget = pinnedResult.data;
        }
      } catch (e) {
        this.error = e.message;
      } finally {
        this.feedLoading = false;
      }
    },

    async loadMore() {
      if (this.feedLoadingMore || !this.hasMore || !this.cursor) return;
      this.feedLoadingMore = true;

      try {
        const result = await fennecService.getFeed(this.cursor, FENNEC_FEED_PAGE_SIZE);
        if (result.success && result.data) {
          this.feed.push(...(result.data.items || []));
          this.cursor = result.data.nextCursor;
          this.hasMore = !!result.data.nextCursor;
        }
      } catch (e) {
        console.error('[Fennec] loadMore error:', e);
      } finally {
        this.feedLoadingMore = false;
      }
    },

    async refreshFeed() {
      this.cursor = null;
      this.hasMore = true;
      await this.loadFeed();
    },

    updatePinnedWidget(widget) {
      this.pinnedWidget = widget;
    },

    updateContext(context) {
      this.context = context;
    },

    handleRouteChange(routeName) {
      this.onFeedPage = FENNEC_FEED_ROUTES.has(routeName);
      this.onAuthPage = FENNEC_AUTH_ROUTES.has(routeName);
      this.syncBodyClass();
    },

    syncBodyClass() {
      const shouldBeActive = this.fennecEnabled && this.featureEnabled && !this.onAuthPage;
      document.body.classList.toggle('fennec-active', shouldBeActive);
    },

    async fetchFennecStoryIds() {
      const result = await fennecService.getFennecStoryIds();
      if (result.success && result.data) {
        this.fennecStoryIds = result.data;
      }
    },

    isStoryFennecTagged(storyId) {
      return this.fennecStoryIds.allFennecStoryIds.includes(storyId);
    },

    isStoryFennecOfficial(storyId) {
      return this.fennecStoryIds.officialStoryIds.includes(storyId);
    },

    markOnboardingShown() {
      this.onboardingShown = true;
      localStorage.setItem('fennec_onboarding_shown', 'true');
    },
  },
});
